/**
 * How much of the ledger the app has asked the server for.
 *
 * `/api/get_expenses` returns every transaction the user has ever had. On the
 * account this was measured against that is 4,589 rows — 6.76 MB of JSON,
 * 2,556 KB gzipped — downloaded before the Overview can finish, on a phone,
 * on every cold start. Trimming it to a trailing window takes it to 1,206 KB.
 *
 * The window is 13 months, and that number is not a guess. Two Overview
 * aggregations walk a trailing **12**-month window:
 *
 *   - `computeSpendingComparison` derives `average_monthly_spending` and
 *     `active_months` from `new Date(y, m - 12, d)` onward.
 *   - `computeTrackedCategories` needs `trailing_12m_total` and
 *     `trailing_3m_avg`.
 *
 * Ask the server for exactly 12 months and a row sitting on the boundary can
 * fall on the wrong side of it, which would quietly shift a "typical month"
 * baseline rather than break anything visibly. The extra month is cheap
 * headroom against that, and it also covers the net-worth chart's 1Y range
 * (365 days) without clipping.
 *
 * Anything that genuinely needs deeper history escalates on demand by calling
 * `requestFullHistory()`. That reverts the request to the unbounded endpoint
 * and every consumer sees the fuller list, so the ledger and the Overview
 * never disagree about which transactions exist.
 *
 * Depth deliberately resets to 'recent' on every boot: a user who once opened
 * 2024 shouldn't pay for all of history on every launch afterwards.
 */
import { computed, readonly, ref } from 'vue'

export type HistoryDepth = 'recent' | 'all'

/** Trailing months fetched by default. See the note above before changing. */
export const RECENT_HISTORY_MONTHS = 13

// Module scope: one depth for the whole app. Escalating anywhere escalates
// everywhere, which is the point — a ledger showing 4,589 rows beside an
// Overview computed from 2,130 would be worse than either on its own.
const depth = ref<HistoryDepth>('recent')

/** Local YYYY-MM-DD. Not toISOString(), which converts to UTC first and can
 *  land on the previous day for anyone west of Greenwich. */
function toLocalIsoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Oldest date the trailing window covers. Callers compare against this to
 *  tell whether a view has navigated past what has actually been fetched. */
export const recentWindowStart = computed(() => {
  const d = new Date()
  d.setMonth(d.getMonth() - RECENT_HISTORY_MONTHS)
  d.setHours(0, 0, 0, 0)
  return d
})

/** The `/api/get_expenses` URL for the current depth. */
export const expensesEndpoint = computed(() => {
  if (depth.value === 'all') return '/api/get_expenses'
  const start = new Date()
  start.setMonth(start.getMonth() - RECENT_HISTORY_MONTHS)
  return `/api/get_expenses?start_date=${toLocalIsoDate(start)}`
})

export function useExpenseHistory() {
  return {
    depth: readonly(depth),
    recentWindowStart,
    isFullHistory: computed(() => depth.value === 'all'),
    /**
     * Ask for the complete ledger. Idempotent, and one-way for the session —
     * nothing drops back to the trailing window, because a view that needed
     * the full set once will need it again if the user returns to it.
     */
    requestFullHistory(): void {
      if (depth.value !== 'all') depth.value = 'all'
    },
  }
}
